import { JobChecklist, QuoteState, QuoteImageItem, QuoteMode, QuoteDevice, QuoteService, QuoteCustomerDeviceAttributes, DeviceAttributeslItem, AttributesInputTypes, CHECKLIST_TYPES, DeviceCheckList, CheckListDetailItem, QuotePart, ComputeData } from "@/models/QuoteState"
import { CustomerDevice, MultupleListItem } from '@/models/Common';
import { MutationTree } from "vuex";
import { generals } from "@puls/puls-general-utils";
import { cloneDeep } from "lodash";
import { QuoteServiceTypes } from "@/components/ui/jobs/JobDetails/CreateQuote/helpers";
import { services } from "@puls/puls-common-constants";

export const QuoteMutations = {
	CHANGE_QUOTE_MODE: 'CHANGE_QUOTE_MODE',
	UPDATE_JOB_IMAGES: 'UPDATE_IMAGES',
	UPDATE_QUOTE_IMAGES: 'UPDATE_QUOTE_IMAGES',
	DELETE_JOB_IMAGE: 'DELETE_JOB_IMAGE',
	DELETE_QUOTE_IMAGE: 'DELETE_QUOTE_IMAGE',
	INIT_QUOTE_DEVICE: 'INIT_QUOTE_DEVICE',
	DELETE_SERVICE: 'DELETE_SERVICE',
	DELETE_PART: 'DELETE_PART',
	INIT_QUOTE_ATTRIBUTES: 'INIT_QUOTE_ATTRIBUTES',
	UPDATE_DEVICE_ATTRIBUTE_VALUE: 'UPDATE_DEVICE_ATTRIBUTE_VALUE',
	UPDATE_DEVICE_ATTRIBUTE_IMAGES: 'UPDATE_DEVICE_ATTRIBUTE_IMAGES',
	DELETE_DEVICE_ATTRIBUTE_IMAGE: 'DELETE_DEVICE_ATTRIBUTE_IMAGE',
	TOGGLE_EDIT_MODE: 'TOGGLE_EDIT_MODE',
	CLEAR_CURRENT_QUOTE: 'CLEAR_CURRENT_QUOTE',
	UPDATE_PROPERTY_IMAGES: 'UPDATE_PROPERTY_IMAGES',
	DELETE_PROPERTY_IMAGE: 'DELETE_PROPERTY_IMAGE',
	INIT_QUOTE_CHECKLIST: 'INIT_QUOTE_CHECKLIST',
	UPDATE_CHECKLIST_IMAGES: 'UPDATE_CHECKLIST_IMAGES',
	DELETE_CHECKLIST_IMAGE: 'DELETE_CHECKLIST_IMAGE',
	UPDATE_CHECKLIST_ITEM_NOTE: 'UPDATE_CHECKLIST_ITEM_NOTE',
	UPDATE_PART: 'UPDATE_PART',
	UPDATE_SERVICE: 'UPDATE_SERVICE',
	UPDATE_CALCULATE_DATA: 'UPDATE_CALCULATE_DATA' 
}
interface DeviceIDPayload {
	customer_device_id: number
}

interface ServicePartUpdatePayload extends DeviceIDPayload {
	uuid: string,
	value: QuoteService | QuotePart
}

interface DeviceAttributePayload extends DeviceIDPayload {
	device_attribute_id: number
}

export const mutations: MutationTree<any> = {
	[QuoteMutations.CHANGE_QUOTE_MODE] (state: QuoteState, payload: QuoteMode) {
		state.mode = payload;
	},
	[QuoteMutations.UPDATE_JOB_IMAGES] (state: QuoteState, payload: QuoteImageItem[]) {	
		state.jobImages = [...state.jobImages, ...payload];
	},
	[QuoteMutations.UPDATE_PROPERTY_IMAGES] (state: QuoteState, payload: QuoteImageItem[]) {	
		state.propertyImages = [...state.propertyImages, ...payload];
	},
	[QuoteMutations.UPDATE_QUOTE_IMAGES] (state: QuoteState, payload: QuoteImageItem[]) {	
		state.quoteImages = [...state.quoteImages, ...payload];
	},
	[QuoteMutations.DELETE_JOB_IMAGE] (state: QuoteState, uuid: string) {
		state.jobImages = filterImageByUuid(state.jobImages, uuid);
	},
	[QuoteMutations.DELETE_PROPERTY_IMAGE] (state: QuoteState, uuid: string) {
		state.propertyImages = filterImageByUuid(state.propertyImages, uuid);
	},
	[QuoteMutations.DELETE_QUOTE_IMAGE] (state: QuoteState, uuid: string) {
		state.quoteImages = filterImageByUuid(state.quoteImages, uuid);
	},
	[QuoteMutations.INIT_QUOTE_DEVICE] (state: QuoteState, payload: CustomerDevice) {
		const deviceIndex: number = state.quoteDevices.findIndex((device: QuoteDevice) => {
			return device.customerDeviceId === payload.customerDeviceId
		})

		if (deviceIndex < 0) {
			state.quoteDevices.push(generateQuoteDevice(payload))
		}
	},
	[QuoteMutations.UPDATE_PART] (state: QuoteState, payload: ServicePartUpdatePayload) {
		try { 
			updateServicePartItem(state, payload, QuoteServiceTypes.PART);
		} catch (error) { 
			// eslint-disable-next-line
			console.error("unable to update part", error)
		}
	},
	[QuoteMutations.UPDATE_SERVICE] (state: QuoteState, payload: ServicePartUpdatePayload) {
		try { 
			updateServicePartItem(state, payload, QuoteServiceTypes.SERVICE);
		} catch (error) { 
			// eslint-disable-next-line
			console.error("unable to update service", error)
		}
	},
	[QuoteMutations.DELETE_PART] (state: QuoteState, payload: ServicePartUpdatePayload) {
		try {
			const { deviceIndex, serviceIndex } = getDeviceServiceIndexs(state, payload);
			state.quoteDevices[deviceIndex].parts.splice(serviceIndex, 1)
		} catch (error) {
			// eslint-disable-next-line
			console.error("unable to delete part price", error)
		}
	},
	[QuoteMutations.DELETE_SERVICE] (state: QuoteState, payload: ServicePartUpdatePayload) {
		try {
			const { deviceIndex, serviceIndex } = getDeviceServiceIndexs(state, payload);
			state.quoteDevices[deviceIndex].services.splice(serviceIndex, 1)
		} catch (error) {
			// eslint-disable-next-line
			console.error("unable to delete part price", error)
		}
	},
	[QuoteMutations.INIT_QUOTE_ATTRIBUTES] (state: QuoteState, payload: CustomerDevice[]) {
		if (state.attributes.length) {
			return;
		}
		
		state.attributes = payload.map((device: CustomerDevice) : QuoteCustomerDeviceAttributes => {
			return {
				title: device.title,
				customer_device_id: device.customerDeviceId,
				attributes: (device.attributes || []).map((attribute: DeviceAttributeslItem): DeviceAttributeslItem => {
					let options: MultupleListItem[] = []		
					if (attribute.options) {
						options = (attribute.options as string[]).map((item: string): MultupleListItem => { 
							return { 
								id: generals.generateUuid(),
								title: item,
								selected: Array.isArray(attribute.value) ? !!attribute.value.find((i: any) => i === item) : false
							}
						})
					}
					return {
						...attribute,
						options,
						value: attribute.value || '',
						images: (attribute.images as string[]).map((image: string): QuoteImageItem => {
							return {
								url: image,
								uuid: generals.generateUuid()
							}
						})
					}
				})
			}
		})
	},
	[QuoteMutations.INIT_QUOTE_CHECKLIST] (state: QuoteState, payload: JobChecklist) {
		const checklists: JobChecklist = cloneDeep(payload);
		transformImages(checklists.post);
		transformImages(checklists.pre);
		state.checklist = checklists;
	},
	[QuoteMutations.UPDATE_DEVICE_ATTRIBUTE_VALUE] (state: QuoteState, payload: DeviceAttributePayload & {value: string}) { 
		try { 
			const deviceAttribute = getDeviceAttribute(state, payload);
			if (deviceAttribute.type === AttributesInputTypes.MULTIPLE_SELECTION) { 
				const opt = ((deviceAttribute.options || []) as MultupleListItem[]).find((item: MultupleListItem) => item.id === payload.value);
				if (opt) opt.selected = !opt.selected;
			} else deviceAttribute.value = payload.value
		} catch (error) {
			// eslint-disable-next-line
			console.error("unable to update attribute value", error)
		}
	},
	[QuoteMutations.UPDATE_DEVICE_ATTRIBUTE_IMAGES] (state: QuoteState, payload: DeviceAttributePayload & {images: QuoteImageItem[]}) {
		try { 
			const deviceAttribute = getDeviceAttribute(state, payload);
			deviceAttribute.images = [...(deviceAttribute.images as QuoteImageItem[]), ...payload.images]
		} catch (error) {
			// eslint-disable-next-line
			console.error("unable to update attribute images", error)
		}
	},
	[QuoteMutations.DELETE_DEVICE_ATTRIBUTE_IMAGE] (state: QuoteState, payload: DeviceAttributePayload & { uuid: string }) {
		try { 
			const deviceAttribute = getDeviceAttribute(state, payload);
			deviceAttribute.images = filterImageByUuid(deviceAttribute.images as QuoteImageItem[], payload.uuid);
		} catch (error) {
			// eslint-disable-next-line
			console.error("unable to delete attribute image", error)
		}
	},
	[QuoteMutations.TOGGLE_EDIT_MODE] (state: QuoteState) {
		state.in_edit_mode = !state.in_edit_mode;
	},
	[QuoteMutations.CLEAR_CURRENT_QUOTE] (state: QuoteState) {
		state.jobImages = [];
		state.propertyImages = [];
		state.attributes = [];
		state.quoteDevices = [];
		state.mode = QuoteMode.SERVICES;
		state.quoteImages = [];
		state.in_edit_mode = true;
		state.checklist = {
			pre: [],
			post: []
		}
		state.computeData = null;
	},
	[QuoteMutations.DELETE_CHECKLIST_IMAGE] (state: QuoteState, { uuid = '', customer_device_id = 0, context = CHECKLIST_TYPES.POST_VISIT, checklist_item_id = 0 }) {
		const checklistDetail = getChecklistDetail(state, context, customer_device_id, checklist_item_id);
		if (checklistDetail) {
			checklistDetail.images = filterImageByUuid(checklistDetail.images as QuoteImageItem[], uuid);
		}
	},
	[QuoteMutations.UPDATE_CHECKLIST_IMAGES] (state: QuoteState, { images = [], customer_device_id = 0, context = CHECKLIST_TYPES.POST_VISIT, checklist_item_id = 0 }) {
		const checklistDetail = getChecklistDetail(state, context, customer_device_id, checklist_item_id);
		if (checklistDetail) {
			checklistDetail.images = [...checklistDetail.images, ...images];
		}
	},
	[QuoteMutations.UPDATE_CHECKLIST_ITEM_NOTE] (state: QuoteState, { note = '', customer_device_id = 0, context = CHECKLIST_TYPES.POST_VISIT, checklist_item_id = 0 }) {
		const checklistDetail = getChecklistDetail(state, context, customer_device_id, checklist_item_id);
		if (checklistDetail) {
			checklistDetail.note = note;
		}
	},
	[QuoteMutations.UPDATE_CALCULATE_DATA] (state: QuoteState, payload: ComputeData) {
		state.computeData = payload;
	} 
}

const updateServicePartStrats: any = {
	[QuoteServiceTypes.SERVICE]: {
		generateItem: generateServiceItem,
		addNewItem: (device: QuoteDevice, item: QuoteService) => {
			device.services.push(generateServiceItem(item));
		},
		updateItem: (device: QuoteDevice, serviceIndex: number, value: QuoteService) => {
			device.services[serviceIndex] = value;
		} 
	},
	[QuoteServiceTypes.PART]: {
		addNewItem: (device: QuoteDevice, item: QuotePart) => {
			device.parts.push(generatePartItem(item));
		},
		updateItem: (device: QuoteDevice, serviceIndex: number, value: QuotePart) => {
			device.parts[serviceIndex] = value;
		} 
	}	
}

function updateServicePartItem (state: QuoteState, payload: ServicePartUpdatePayload, type: string) {
	const strat = updateServicePartStrats[type];
	if (!payload.value?.uuid) {
		const device = state.quoteDevices.find((device: QuoteDevice) => {
			return device.customerDeviceId === payload.customer_device_id
		})
		strat.addNewItem(device, payload.value)
	} else { 
		const { deviceIndex, serviceIndex } = getDeviceServiceIndexs(state, payload);
		const device = state.quoteDevices[deviceIndex];
		strat.updateItem(device, serviceIndex, payload.value);
	}
}

function getDeviceAttribute (state: QuoteState, payload: any) { 
	const { deviceIndex, attributeIndex } = getDeviceAttributeIndexes(state, payload)
	const deviceAttr = state.attributes[deviceIndex].attributes || [];
	return deviceAttr[attributeIndex] || {};
}

function filterImageByUuid (array: QuoteImageItem[], uuid: string) { 
	return array.filter((img: QuoteImageItem) => img.uuid !== uuid);
}

function getDeviceAttributeIndexes (state: QuoteState, payload: DeviceAttributePayload) {
	try { 
		const deviceIndex = state.attributes.findIndex((device: QuoteCustomerDeviceAttributes) => {
			return device.customer_device_id === payload.customer_device_id
		})

		const attributes = state.attributes[deviceIndex].attributes;
		let attributeIndex = -1;
		if (attributes) { 
			attributeIndex = attributes.findIndex((attr: DeviceAttributeslItem) => {
				return attr.device_attribute_id === payload.device_attribute_id
			})
		}			
		return { deviceIndex, attributeIndex };
	} catch (err) {
		return { deviceIndex: -1, attributeIndex: -1 }
	}
}

function generateServiceItem ({ title, price }: { title?: string, price?: string}): QuoteService {
	return {
		uuid: generals.generateUuid(),
		id: services.GENERIC_REPAIR_SERVICE,
		title: title || '',
		price: price || ''
	}
}

function generatePartItem ({ title, sku, price }: { title?: string, sku?: string, price?: string}): QuotePart {
	return {
		uuid: generals.generateUuid(),
		id: 1728,
		title: title || '',
		sku: sku || generals.generateUuid(),
		price: price || ''
	}
}

function getDeviceIndex (state: QuoteState, customer_device_id: number) : number {
	return state.quoteDevices.findIndex((device: QuoteDevice) => {
		return device.customerDeviceId === customer_device_id
	})
}

function getServiceIndex (state: QuoteState, deviceIndex: number, uuid: string) : number {
	let index = state.quoteDevices[deviceIndex].services.findIndex((service: QuoteService) => {
		return service.uuid === uuid
	})

	if (index < 0) {
		index = state.quoteDevices[deviceIndex].parts.findIndex((service: QuoteService) => {
			return service.uuid === uuid
		})
	}

	return index;
}

function getDeviceServiceIndexs (state: QuoteState, payload: ServicePartUpdatePayload) {
	try {
		const deviceIndex = getDeviceIndex(state, payload.customer_device_id);
		const serviceIndex = getServiceIndex(state, deviceIndex, payload.uuid);

		return { deviceIndex, serviceIndex }	
	} catch (error) {
		return { deviceIndex: -1, serviceIndex: -1 }
	}
}

function generateQuoteDevice (payload: CustomerDevice) : QuoteDevice {
	return {
		services: [],
		parts: [],
		customerDeviceId: payload.customerDeviceId
	}
}

function getDeviceChecklist (state: QuoteState, type: CHECKLIST_TYPES, customer_device_id: number) : DeviceCheckList | null {
	const context : DeviceCheckList[] | undefined = type === CHECKLIST_TYPES.POST_VISIT ? state.checklist?.post : state.checklist?.pre;
	if (!context) {
		return null;
	}
	const dev = context.find((device: DeviceCheckList) => device.customer_device_id === customer_device_id)

	return dev || null;
}

function getChecklistDetail (state: QuoteState, context: CHECKLIST_TYPES, customer_device_id: number, checklist_item_id: number) : CheckListDetailItem | null {
	try {
		const device: DeviceCheckList | null = getDeviceChecklist(state, context, customer_device_id);
		if (!device) {
			return null;
		}

		return device.checklist_details.find((detail: CheckListDetailItem) => detail.checklist_item_id === checklist_item_id) || null;
	} catch (error) {
		return null;
	}
}

function transformImages (lists: DeviceCheckList[]) {
	lists.forEach((list: DeviceCheckList) => {
		list.checklist_details.forEach((detail: CheckListDetailItem) => {
			detail.images.map((image: QuoteImageItem | string) => {
				if (typeof image === 'string') {
					return {
						url: image,
						uuid: generals.generateUuid()
					}
				}

				return image
			})
		});
	})
}
